import React, { FC, useCallback, useMemo } from 'react'
import { DisputeInfoStep, useDisputeInfoModalContext } from '../DisputeInfoContext'
import { Button, Grid, GridWall, Icon, Link, Text } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../utils/responsive'
import { useTranslation } from 'next-i18next'
import { Attachment, Comment, Identity } from '@afterpaytouch/portal-api'
import {
  getAttachmentName,
  isDisputeClosed,
  isDisputePendingConsumerResponse,
  isDisputePendingConsumerResubmitEvidence,
  isDisputePendingRepresentmentReview,
} from '../utils'
import { useProcessDispute } from '../hooks'
import { TrackOnMount, useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import { LaunchDarklyFlags, useFlag } from '../../../hooks'

const I18N_NAMESPACE = ['dispute']

interface CommentWithAttachments extends Comment {
  attachments: Array<{ url: string; name: string; id: number }>
}

export const Detail: FC = () => {
  const { textSize, smallTextSize } = useConsumerSizes()
  const { t } = useTranslation(I18N_NAMESPACE)
  const { disputeInfo, order, closeModal, setStep, setIsSubmitEvidence } = useDisputeInfoModalContext()
  const { cancel, accept, isProcessError } = useProcessDispute()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const pushbackDisputeEnabled = useFlag(LaunchDarklyFlags.IrPushBackDisputeConsumerEnabled)

  /* memorized text */
  const state = useMemo(() => {
    return disputeInfo?.disputeEntities[0]?.consumerDisplayState?.replace(/_/g, ' ').toLowerCase()
  }, [disputeInfo])
  const createdAt = useMemo(() => {
    return new Date(disputeInfo?.disputeEntities[0]?.createdAt).toLocaleDateString()
  }, [disputeInfo])
  const description = useMemo(() => {
    return disputeInfo?.disputeEntities[0]?.subject
  }, [disputeInfo])
  const comments: CommentWithAttachments[] = useMemo(() => {
    return disputeInfo?.disputeEntities[0]?.comments?.map((comment) => {
      const attachments = disputeInfo?.disputeEntities[0]?.attachments
        ?.filter((attachment: Attachment) => {
          return attachment.commentId === comment.id
        })
        .map((attachment: Attachment) => {
          return {
            url: attachment.evidenceLink,
            name: getAttachmentName(attachment.createdAt),
            id: attachment.id,
          }
        })
      return {
        ...comment,
        attachments,
      }
    })
  }, [disputeInfo])
  const close = useMemo(() => {
    return isDisputeClosed(disputeInfo)
  }, [disputeInfo])
  const pendingConsumerResponse = useMemo(() => {
    return isDisputePendingConsumerResponse(disputeInfo)
  }, [disputeInfo])
  const pendingConsumerResubmitEvidence = useMemo(() => {
    return isDisputePendingConsumerResubmitEvidence(disputeInfo)
  }, [disputeInfo])
  const pendingRepresentmentReview = useMemo(() => {
    return isDisputePendingRepresentmentReview(disputeInfo)
  }, [disputeInfo])

  /* memorized function */
  const getCommentName = useCallback(
    (identity: Identity) => {
      switch (identity) {
        case Identity.Merchant:
          return order?.merchant?.merchantDisplayName ?? order?.merchant?.name
        case Identity.Admin:
          return order?.merchant?.countryCode === 'GB' ? 'Clearpay' : 'Afterpay'
        case Identity.Consumer:
          return 'You'
        default:
          return ''
      }
    },
    [order]
  )
  const getCommentDate = useCallback((timestamp: number) => {
    const date = new Date(timestamp)
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }, [])
  const submitEvidenceHandler = useCallback(() => {
    setStep(DisputeInfoStep.SubmitEvidence)
    setIsSubmitEvidence(true)
  }, [setIsSubmitEvidence, setStep])
  const cancelThisDisputeHandler = useCallback(() => {
    cancel(disputeInfo?.disputeEntities[0]?.id)
    logEvent(TrackingEvent.PRESSED_DISPUTE_INFO_CANCEL)
  }, [cancel, disputeInfo?.disputeEntities, logEvent])
  const acceptThisDisputeHandler = useCallback(() => {
    accept(disputeInfo?.disputeEntities[0]?.id)
    logEvent(TrackingEvent.PRESSED_DISPUTE_INFO_ACCEPT)
  }, [accept, disputeInfo?.disputeEntities, logEvent])
  const handleRejectThisDisputeHandler = useCallback(() => {
    setStep(DisputeInfoStep.SubmitEvidence)
    setIsSubmitEvidence(false)
    logEvent(TrackingEvent.PRESSED_DISPUTE_INFO_REJECT)
  }, [setStep, setIsSubmitEvidence, logEvent])

  /* render cta */
  const renderCTA = useCallback(() => {
    if (close || pendingRepresentmentReview) {
      return (
        <Button padding='Fluid' onClick={closeModal}>
          {t('dispute:info:detail:cta:close')}
        </Button>
      )
    }

    if (pendingConsumerResponse) {
      return (
        <>
          <Button padding='Fluid' onClick={acceptThisDisputeHandler}>
            {t('dispute:info:detail:cta:accept')}
          </Button>
          <Button padding='Fluid' onClick={handleRejectThisDisputeHandler}>
            {t('dispute:info:detail:cta:reject')}
          </Button>
        </>
      )
    }

    return (
      <>
        {pushbackDisputeEnabled && pendingConsumerResubmitEvidence && (
          <Button padding='Fluid' onClick={submitEvidenceHandler}>
            {t('dispute:info:detail:cta:submitEvidence')}
          </Button>
        )}
        <Button padding='Fluid' onClick={cancelThisDisputeHandler}>
          {t('dispute:info:detail:cta:cancel')}
        </Button>
      </>
    )
  }, [
    acceptThisDisputeHandler,
    cancelThisDisputeHandler,
    close,
    closeModal,
    handleRejectThisDisputeHandler,
    pendingConsumerResponse,
    pendingConsumerResubmitEvidence,
    pendingRepresentmentReview,
    pushbackDisputeEnabled,
    submitEvidenceHandler,
    t,
  ])

  return (
    <>
      <TrackOnMount eventName={TrackingEvent.VIEWED_DISPUTE_INFO_DETAIL} />
      <GridWall columns={2} gutter={'none'}>
        <Text size={textSize} bold>
          {t('dispute:info:detail.status')}
        </Text>
        <Text size={textSize}>{state}</Text>
        <div className='mt-2'>
          <Text size={textSize} bold>
            {t('dispute:info:detail:createdAt')}
          </Text>
        </div>
        <div className='mt-2'>
          <Text size={textSize}>{createdAt}</Text>
        </div>
        <div className='mt-2'>
          <Text size={textSize} bold>
            {t('dispute:info:detail:description')}
          </Text>
        </div>
        <div className='mt-2 whitespace-pre-line'>
          <Text size={textSize}>{description}</Text>
        </div>
      </GridWall>
      <div className='my-4'>
        <div className='mb-2.5'>
          <Text size={textSize} bold>
            {t('dispute:info:detail:reportDetail')}
          </Text>
        </div>
        {comments.map((comment) => {
          return (
            <div key={comment.id} className='border-dark-subtle rounded-sm border border-solid p-4'>
              <Grid>
                <Text size={textSize} bold color={'Orca'}>
                  {getCommentName(comment.identity)}
                </Text>
                <Text size={textSize} color={'Gray40'}>
                  {getCommentDate(comment.createdAt)}
                </Text>
              </Grid>
              {comment.content && comment.content.trim() !== '' && (
                <div className='whitespace-pre-line break-words'>
                  <Text size={textSize}>{comment.content.trim()}</Text>
                </div>
              )}
              {comment.attachments?.map(({ url, name, id }) => {
                return (
                  <div key={id}>
                    <Link href={url} target='_blank'>
                      <Icon.Attachment size={'Small'} />
                      <Text size={textSize} color='Orca'>
                        {name}
                      </Text>
                    </Link>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
      {isProcessError && (
        <div className='mb-4'>
          <Text size={smallTextSize} color='Fire'>
            {t('dispute:info:processMessage:error')}
          </Text>
        </div>
      )}
      {renderCTA()}
    </>
  )
}
